import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const AdminMessages = () => {
    const [messages, setMessages] = useState([]);
    const [stats, setStats] = useState({ new: 0, reviewed: 0, total: 0 });
    const [loading, setLoading] = useState(true);
    const [filter, setFilter] = useState('all');
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');

    const fetchMessages = useCallback(async () => {
        try {
            setLoading(true);
            const response = await axios.get(`/api/admin/feedback?status=${filter}`);
            setMessages(response.data || []);
        } catch (err) {
            console.error('Failed to fetch feedback messages:', err);
            setError('Failed to load messages');
        } finally {
            setLoading(false);
        }
    }, [filter]);

    const fetchStats = async () => {
        try {
            const response = await axios.get('/api/admin/feedback/stats');
            setStats(response.data);
        } catch (err) {
            console.error('Failed to fetch feedback stats:', err);
        }
    };

    useEffect(() => {
        fetchMessages();
        fetchStats();
    }, [fetchMessages]);

    const handleMarkReviewed = async (id) => {
        try {
            setError('');
            setSuccess('');
            await axios.put(`/api/admin/feedback/${id}`, { status: 'reviewed' });
            setSuccess('Marked as reviewed');
            fetchMessages();
            fetchStats();
        } catch (err) {
            setError('Failed to update status');
        }
    };

    const handleMarkNew = async (id) => {
        try {
            setError('');
            setSuccess('');
            await axios.put(`/api/admin/feedback/${id}`, { status: 'new' });
            setSuccess('Marked as new');
            fetchMessages();
            fetchStats();
        } catch (err) {
            setError('Failed to update status');
        }
    };

    const handleDelete = async (id) => {
        if (!window.confirm('Are you sure you want to delete this message?')) return;
        try {
            setError('');
            setSuccess('');
            await axios.delete(`/api/admin/feedback/${id}`);
            setSuccess('Message deleted');
            fetchMessages();
            fetchStats();
        } catch (err) {
            setError('Failed to delete message');
        }
    };

    const formatDate = (dateStr) => {
        if (!dateStr) return '-';
        const date = new Date(dateStr);
        return date.toLocaleString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
            timeZone: 'Asia/Jakarta'
        });
    };

    return (
        <div>
            <div className="section-header">
                <h2>📬 User Feedback Messages</h2>
            </div>

            {/* Stats cards */}
            <div style={{
                display: 'flex',
                gap: '15px',
                marginBottom: '20px',
                flexWrap: 'wrap'
            }}>
                <div style={{
                    backgroundColor: '#e3f2fd',
                    padding: '15px 25px',
                    borderRadius: '10px',
                    textAlign: 'center',
                    minWidth: '120px'
                }}>
                    <div style={{ fontSize: '28px', fontWeight: 'bold', color: '#1976d2' }}>{stats.total}</div>
                    <div style={{ fontSize: '13px', color: '#666' }}>Total Messages</div>
                </div>
                <div style={{
                    backgroundColor: '#fff3e0',
                    padding: '15px 25px',
                    borderRadius: '10px',
                    textAlign: 'center',
                    minWidth: '120px'
                }}>
                    <div style={{ fontSize: '28px', fontWeight: 'bold', color: '#f57c00' }}>{stats.new}</div>
                    <div style={{ fontSize: '13px', color: '#666' }}>New</div>
                </div>
                <div style={{
                    backgroundColor: '#e8f5e9',
                    padding: '15px 25px',
                    borderRadius: '10px',
                    textAlign: 'center',
                    minWidth: '120px'
                }}>
                    <div style={{ fontSize: '28px', fontWeight: 'bold', color: '#388e3c' }}>{stats.reviewed}</div>
                    <div style={{ fontSize: '13px', color: '#666' }}>Reviewed</div>
                </div>
            </div>

            {/* Alerts */}
            {error && <div className="error" style={{ marginBottom: '15px' }}>{error}</div>}
            {success && <div className="success" style={{ marginBottom: '15px' }}>{success}</div>}

            {/* Filter */}
            <div style={{
                backgroundColor: '#f9f9f9',
                padding: '15px',
                borderRadius: '8px',
                marginBottom: '20px',
                border: '1px solid #e0e0e0'
            }}>
                <div style={{ display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap' }}>
                    <label style={{ fontWeight: '600', fontSize: '14px' }}>Filter by status:</label>
                    <button
                        onClick={() => setFilter('all')}
                        style={{
                            padding: '8px 16px',
                            borderRadius: '20px',
                            border: 'none',
                            cursor: 'pointer',
                            fontWeight: '500',
                            backgroundColor: filter === 'all' ? '#1976d2' : '#e0e0e0',
                            color: filter === 'all' ? 'white' : '#333'
                        }}
                    >
                        All ({stats.total})
                    </button>
                    <button
                        onClick={() => setFilter('new')}
                        style={{
                            padding: '8px 16px',
                            borderRadius: '20px',
                            border: 'none',
                            cursor: 'pointer',
                            fontWeight: '500',
                            backgroundColor: filter === 'new' ? '#f57c00' : '#e0e0e0',
                            color: filter === 'new' ? 'white' : '#333'
                        }}
                    >
                        New ({stats.new})
                    </button>
                    <button
                        onClick={() => setFilter('reviewed')}
                        style={{
                            padding: '8px 16px',
                            borderRadius: '20px',
                            border: 'none',
                            cursor: 'pointer',
                            fontWeight: '500',
                            backgroundColor: filter === 'reviewed' ? '#388e3c' : '#e0e0e0',
                            color: filter === 'reviewed' ? 'white' : '#333'
                        }}
                    >
                        Reviewed ({stats.reviewed})
                    </button>
                </div>
            </div>

            {/* Messages list */}
            {loading ? (
                <p>Loading messages...</p>
            ) : messages.length === 0 ? (
                <div style={{
                    textAlign: 'center',
                    padding: '40px',
                    color: '#666',
                    backgroundColor: '#f9f9f9',
                    borderRadius: '10px'
                }}>
                    <div style={{ fontSize: '48px', marginBottom: '10px' }}>📭</div>
                    <p>No feedback messages yet</p>
                </div>
            ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
                    {messages.map(msg => (
                        <div
                            key={msg.id}
                            style={{
                                backgroundColor: 'white',
                                border: `2px solid ${msg.status === 'new' ? '#f57c00' : '#e0e0e0'}`,
                                borderRadius: '12px',
                                padding: '20px',
                                boxShadow: '0 2px 4px rgba(0,0,0,0.05)'
                            }}
                        >
                            <div style={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'flex-start',
                                marginBottom: '12px',
                                flexWrap: 'wrap',
                                gap: '10px'
                            }}>
                                <div>
                                    <div style={{ fontWeight: '600', fontSize: '15px', color: '#333' }}>
                                        {msg.user_name || 'Unknown User'}
                                    </div>
                                    <div style={{ fontSize: '13px', color: '#666' }}>
                                        {msg.user_email}
                                    </div>
                                </div>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                    <span style={{
                                        padding: '4px 12px',
                                        borderRadius: '15px',
                                        fontSize: '12px',
                                        fontWeight: '600',
                                        backgroundColor: msg.status === 'new' ? '#fff3e0' : '#e8f5e9',
                                        color: msg.status === 'new' ? '#f57c00' : '#388e3c'
                                    }}>
                                        {msg.status === 'new' ? '🆕 New' : '✅ Reviewed'}
                                    </span>
                                    <span style={{ fontSize: '12px', color: '#999' }}>
                                        {formatDate(msg.created_at)}
                                    </span>
                                </div>
                            </div>

                            <div style={{
                                backgroundColor: '#f8f9fa',
                                padding: '15px',
                                borderRadius: '8px',
                                marginBottom: '15px',
                                borderLeft: '4px solid #00AEEF',
                                fontSize: '14px',
                                lineHeight: '1.6',
                                whiteSpace: 'pre-wrap'
                            }}>
                                {msg.message}
                            </div>

                            {msg.reviewed_at && (
                                <div style={{ fontSize: '12px', color: '#999', marginBottom: '10px' }}>
                                    Reviewed on {formatDate(msg.reviewed_at)} by {msg.reviewed_by}
                                </div>
                            )}

                            <div style={{ display: 'flex', gap: '10px' }}>
                                {msg.status === 'new' ? (
                                    <button
                                        onClick={() => handleMarkReviewed(msg.id)}
                                        style={{
                                            padding: '8px 16px',
                                            borderRadius: '6px',
                                            border: 'none',
                                            cursor: 'pointer',
                                            backgroundColor: '#388e3c',
                                            color: 'white',
                                            fontSize: '13px',
                                            fontWeight: '500'
                                        }}
                                    >
                                        ✓ Mark as Reviewed
                                    </button>
                                ) : (
                                    <button
                                        onClick={() => handleMarkNew(msg.id)}
                                        style={{
                                            padding: '8px 16px',
                                            borderRadius: '6px',
                                            border: '1px solid #f57c00',
                                            cursor: 'pointer',
                                            backgroundColor: 'white',
                                            color: '#f57c00',
                                            fontSize: '13px',
                                            fontWeight: '500'
                                        }}
                                    >
                                        ↩ Mark as New
                                    </button>
                                )}
                                <button
                                    onClick={() => handleDelete(msg.id)}
                                    style={{
                                        padding: '8px 16px',
                                        borderRadius: '6px',
                                        border: '1px solid #dc3545',
                                        cursor: 'pointer',
                                        backgroundColor: 'white',
                                        color: '#dc3545',
                                        fontSize: '13px',
                                        fontWeight: '500'
                                    }}
                                >
                                    🗑️ Delete
                                </button>
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

export default AdminMessages;
